import {
    ButtonBase,
    Checkbox,
    DialogContent,
    FormControlLabel,
    FormGroup,
    TextField,
    IconButton,
} from "@mui/material";
import _, { toInteger } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import NumPad from "react-numpad";
import { enqueueSnackbar } from "notistack";
import { arrayMove } from "react-sortable-hoc";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../AppContext";
import backIcon from "../../../../images/back-icon.svg";
import { ItemDialog, ItemSmallButton, useStyles } from "./style";
import { PosContext } from "../../../../shared/PosContext";
import { isRoleAdmin } from "../../../../frappeConfig";
import BundleConfigurator from "./BundleConfigurator";
import { useSaveUserSettings } from "../../../../shared/hooks/useFrappeDoctype";

const normalizeBundle = (raw) => {
    if (!raw) return null;
    const categories = Array.isArray(raw.categories) ? raw.categories : [];

    return {
        ...raw,
        categories: categories.map((cat) => {
            const allow_multiple_select =
                cat?.allow_multiple_select === undefined
                    ? false
                    : !!cat.allow_multiple_select;
            const mandatory =
                cat?.mandatory === undefined ? true : !!cat.mandatory;
            const options = Array.isArray(cat?.options) ? cat.options : [];

            return {
                ...cat,
                allow_multiple_select,
                mandatory,
                options: options.map((opt) => ({
                    ...opt,
                    default: opt?.default === undefined ? false : !!opt.default,
                    qty: opt?.qty === undefined ? 1 : opt.qty,
                })),
            };
        }),
    };
};

const pickDefaultsForCategory = (cat) => {
    const options = Array.isArray(cat?.options) ? cat.options : [];
    if (!options.length) return cat?.allow_multiple_select ? [] : null;

    const allowMulti = !!cat?.allow_multiple_select;
    const mandatory = cat?.mandatory === undefined ? true : !!cat.mandatory;

    if (allowMulti) {
        const defaults = options.filter((o) => !!o.default);
        // If there is no explicit default, never auto-select (even if mandatory)
        if (defaults.length) return defaults;
        return [];
    }

    // Single-select + optional: never auto-select anything (ignore default=true)
    if (!mandatory) return null;

    const defaults = options.filter((o) => !!o.default);
    // If there is no explicit default, never auto-select (even if mandatory)
    if (defaults.length >= 1) return defaults[0];
    return null;
};

const extractSelectedOptionCodes = (selectedOptions) => {
    const codes = new Set();
    if (!selectedOptions) return codes;
    Object.values(selectedOptions).forEach((v) => {
        if (Array.isArray(v)) {
            v.forEach((o) => o?.item_code && codes.add(o.item_code));
        } else if (v?.item_code) {
            codes.add(v.item_code);
        }
    });
    return codes;
};

function AddItemWidget({
    item,
    onClose,
    open,
    onAddItem,
    onUpdateItem,
    pushItemWorker,
    editedOrderItem,
}) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [bundleStep, setBundleStep] = useState(0);
    const [bundleErrors, setBundleErrors] = useState({});
    const [reorderMode, setReorderMode] = useState(false);

    const { userInfo, elevatedPrivileges, isTabsPos, userMenuSettings, setUserMenuSettings } =
        useContext(AppContext);
    const saveUserSettings = useSaveUserSettings();

    const { addonsItems } = useContext(PosContext);

    const addonItems = useMemo(() => {
        if (addonsItems) {
            return addonsItems;
        }
        return [];
    }, [addonsItems]);
    const menuBundle = useMemo(() => {
        try {
            if (item && item.menu_item_bundle) {
                const parsed = JSON.parse(item.menu_item_bundle);
                if (
                    parsed &&
                    Array.isArray(parsed.categories) &&
                    parsed.categories.length > 0
                ) {
                    return normalizeBundle(parsed);
                }
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return normalizeBundle({ categories: parsed });
                }
            }
        } catch (e) {}
        return null;
    }, [item]);

    const applyBundleOrderFromSettings = useCallback((bundle) => {
        if (!bundle || !Array.isArray(bundle.categories) || bundle.categories.length === 0) {
            return bundle;
        }
        const itemCode = item?.item_code;
        if (!itemCode || !userMenuSettings) return bundle;

        const entry = userMenuSettings?.bundle_sort_indices?.[itemCode];
        if (!entry) return bundle;

        const catOrder = Array.isArray(entry?.categories) ? entry.categories : [];
        const catIndexMap = catOrder.reduce((acc, x) => {
            if (x?.name) acc[x.name] = Number(x.index);
            return acc;
        }, {});

        const originalCats = bundle.categories || [];
        const sortedCats = [...originalCats]
            .map((c, originalIndex) => ({ c, originalIndex }))
            .sort((a, b) => {
                const ia = catIndexMap[a.c?.name];
                const ib = catIndexMap[b.c?.name];
                const ra = Number.isFinite(ia) ? ia : 10_000 + a.originalIndex;
                const rb = Number.isFinite(ib) ? ib : 10_000 + b.originalIndex;
                return ra - rb;
            })
            .map(({ c }) => c);

        const optionOrderByCategory = entry?.options || {};

        const finalCats = sortedCats.map((cat) => {
            const catName = cat?.name;
            const optOrder = Array.isArray(optionOrderByCategory?.[catName])
                ? optionOrderByCategory[catName]
                : [];
            const optIndexMap = optOrder.reduce((acc, x) => {
                if (x?.item_code) acc[x.item_code] = Number(x.index);
                return acc;
            }, {});
            const opts = Array.isArray(cat?.options) ? cat.options : [];
            const sortedOpts = [...opts]
                .map((o, originalIndex) => ({ o, originalIndex }))
                .sort((a, b) => {
                    const ia = optIndexMap[a.o?.item_code];
                    const ib = optIndexMap[b.o?.item_code];
                    const ra = Number.isFinite(ia) ? ia : 10_000 + a.originalIndex;
                    const rb = Number.isFinite(ib) ? ib : 10_000 + b.originalIndex;
                    return ra - rb;
                })
                .map(({ o }) => o);
            return { ...cat, options: sortedOpts };
        });

        return { ...bundle, categories: finalCats };
    }, [item?.item_code, userMenuSettings]);

    // Local mutable bundle copy (for reorder mode). This is NOT persisted here; persistence is added in a later todo.
    const [bundleDraft, setBundleDraft] = useState(null);
    useEffect(() => {
        setBundleDraft(applyBundleOrderFromSettings(menuBundle));
    }, [menuBundle, applyBundleOrderFromSettings]);

    const persistBundleSortSetting = (nextSettings) => {
        if (!nextSettings) return;
        setUserMenuSettings?.(nextSettings);
        saveUserSettings.mutate({
            doctype: "menu_settings",
            user_settings: nextSettings,
        });
    };

    const persistCategoryOrderForItem = (categories) => {
        const itemCode = item?.item_code;
        if (!itemCode || !userMenuSettings) return;
        const next = { ...(userMenuSettings || {}) };
        next.bundle_sort_indices = { ...(next.bundle_sort_indices || {}) };
        const entry = { ...(next.bundle_sort_indices[itemCode] || {}) };
        entry.categories = (categories || [])
            .filter((c) => c?.name)
            .map((c, index) => ({ name: c.name, index }));
        entry.options = { ...(entry.options || {}) };
        next.bundle_sort_indices[itemCode] = entry;
        persistBundleSortSetting(next);
    };

    const persistOptionOrderForItem = (categoryName, options) => {
        const itemCode = item?.item_code;
        if (!itemCode || !userMenuSettings || !categoryName) return;
        const next = { ...(userMenuSettings || {}) };
        next.bundle_sort_indices = { ...(next.bundle_sort_indices || {}) };
        const entry = { ...(next.bundle_sort_indices[itemCode] || {}) };
        entry.categories = Array.isArray(entry.categories) ? entry.categories : [];
        entry.options = { ...(entry.options || {}) };
        entry.options[categoryName] = (options || [])
            .filter((o) => o?.item_code)
            .map((o, index) => ({ item_code: o.item_code, index }));
        next.bundle_sort_indices[itemCode] = entry;
        persistBundleSortSetting(next);
    };


    const hasExtraAddons = useMemo(() => {
        return Array.isArray(item?.allowed_addons) && item.allowed_addons.length > 0;
    }, [item]);
    const isNotesOnly = useMemo(() => {
        return !(menuBundle && menuBundle.categories) && !hasExtraAddons;
    }, [menuBundle, hasExtraAddons]);

    const [qty, setQty] = useState(1);
    const [notes, setNotes] = useState("");
    const [isEditingPrice, setIsEditingPrice] = useState(false);

    const { menuAddons } = useContext(AppContext);

    const [itemRate, setItemRate] = useState(0);
    const [total, setTotal] = useState(0);
    const [addonsTotal, setAddonsTotal] = useState(0);

    const reset = () => {
        setSelectedAddons([]);
        setSelectedOptions({});
        setQty(1);
        setNotes("");
        setItemRate(0);
        setTotal(0);
        setAddonsTotal(0);
        setIsEditingPrice(false);
        setBundleErrors({});
    };



    useEffect(() => {
        if (editedOrderItem) {
            setItemRate(editedOrderItem.item.standard_rate);
            const tmpQty = editedOrderItem.qty;
            setNotes(editedOrderItem.notes);
            setQty(tmpQty);

            // Split addons into options vs extra addons based on menu bundle / allowed_addons
            const existingAddons = Array.isArray(editedOrderItem.addons)
                ? editedOrderItem.addons
                : [];

            const allowedAddons = item?.allowed_addons || [];

            // Build defaults
            const optionsMap = {};
            if (menuBundle && menuBundle.categories) {
                menuBundle.categories.forEach((cat) => {
                    const allowMulti = !!cat.allow_multiple_select;
                    const opts = Array.isArray(cat.options) ? cat.options : [];

                    if (!opts.length) return;

                    if (allowMulti) {
                        const selected = opts.filter((o) =>
                            existingAddons.some(
                                (ad) => ad.item_code === o.item_code
                            )
                        );
                        const computed =
                            selected.length > 0
                                ? selected
                                : pickDefaultsForCategory(cat);
                        optionsMap[cat.name] = Array.isArray(computed)
                            ? computed
                            : computed
                            ? [computed]
                            : [];
                    } else {
                        const found = existingAddons.find((ad) =>
                            opts.some((opt) => opt.item_code === ad.item_code)
                        );
                        if (found) {
                            const opt = opts.find(
                                (o) => o.item_code === found.item_code
                            );
                            optionsMap[cat.name] = opt || pickDefaultsForCategory(cat);
                        } else {
                            optionsMap[cat.name] = pickDefaultsForCategory(cat);
                        }
                    }
                });
            }

            setSelectedOptions(optionsMap);
            // Extra addons: those that are in allowed_addons but not part of options
            const optionCodes = extractSelectedOptionCodes(optionsMap);
            const extraAddons = existingAddons
                .map((i) => i.item_code)
                .filter((code) => allowedAddons.includes(code) && !optionCodes.has(code));
            setSelectedAddons(extraAddons);
        } else {
            if (item) {
                const { standard_rate, notes } = item;
                setItemRate(standard_rate);
                setNotes(notes);
                setQty(1);
                setTotal(standard_rate * 1);

                // Default select first option of each category
                if (menuBundle && menuBundle.categories) {
                    const defaults = {};
                    menuBundle.categories.forEach((cat) => {
                        defaults[cat.name] = pickDefaultsForCategory(cat);
                    });
                    setSelectedOptions(defaults);
                } else {
                    setSelectedOptions({});
                }
            } else {
                setItemRate(0);
                setQty(1);
                setNotes("");
                setSelectedOptions({});
            }
        }
        setBundleStep(0);
        setBundleErrors({});
        setReorderMode(false);
    }, [item, open, editedOrderItem, menuBundle]);

    useEffect(() => {
        const calculateAddonsTotal = () => {
            let computedAddonsTotal = 0;
            const base = itemRate * qty;

            // Extra addons (from allowed_addons) use system addon rates
            if (menuAddons && selectedAddons && selectedAddons.length > 0) {
                selectedAddons.forEach((oneItem) => {
                    const foundItem = _.find(menuAddons, {
                        item_code: oneItem,
                    });
                    if (foundItem) {
                        computedAddonsTotal +=
                            (foundItem.standard_rate || 0) * qty;
                    }
                });
            }

            // Options (from menu_item_bundle) use added_price
            if (selectedOptions && Object.keys(selectedOptions).length > 0) {
                Object.values(selectedOptions).forEach((optOrList) => {
                    const list = Array.isArray(optOrList)
                        ? optOrList
                        : optOrList
                        ? [optOrList]
                        : [];
                    list.forEach((opt) => {
                        const added = parseFloat(opt?.added_price) || 0;
                        const optQty = parseFloat(opt?.qty) || 1;
                        computedAddonsTotal += added * qty * optQty;
                    });
                });
            }

            setTotal(base + computedAddonsTotal);
            setAddonsTotal(computedAddonsTotal);
        };

        const timer = setTimeout(calculateAddonsTotal, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [qty, selectedAddons, selectedOptions, itemRate, menuAddons]);

    const handleNotesChange = (event) => {
        setNotes(event.target.value);
    };

    const addQty = (addedQty) => {
        if (qty + addedQty > 0) setQty(qty + addedQty);
    };

    const handleClose = () => {
        reset();
        if (onClose) {
            onClose();
        }
    };

    const handleClick = () => {
        // Validate mandatory bundle categories before allowing save
        if (menuBundle && Array.isArray(menuBundle.categories)) {
            const nextErrors = {};
            let firstMissingIdx = -1;

            menuBundle.categories.forEach((cat, idx) => {
                const mandatory =
                    cat?.mandatory === undefined ? true : !!cat?.mandatory;
                if (!mandatory) return;

                const allowMulti = !!cat?.allow_multiple_select;
                const selected = selectedOptions?.[cat?.name];
                const hasSelection = allowMulti
                    ? Array.isArray(selected) && selected.length > 0
                    : !!selected?.item_code;

                if (!hasSelection) {
                    if (firstMissingIdx < 0) firstMissingIdx = idx;
                    if (cat?.name) nextErrors[cat.name] = t("bundle.required");
                }
            });

            if (firstMissingIdx >= 0) {
                setBundleErrors(nextErrors);
                const missingCat = menuBundle.categories[firstMissingIdx];
                setBundleStep(firstMissingIdx);
                enqueueSnackbar(
                    t("bundle.selectOptionFor", {
                        category:
                            missingCat?.name || t("bundle.requiredCategory"),
                    }),
                    { variant: "warning" }
                );
                return;
            } else if (Object.keys(bundleErrors || {}).length > 0) {
                setBundleErrors({});
            }
        }

        if (editedOrderItem) {
            const updatedEntry = {
                ...editedOrderItem,
                qty,
                addons: (() => {
                    const optionsAddons = Object.values(
                        selectedOptions || {}
                    )
                        .flatMap((optOrList) =>
                            Array.isArray(optOrList)
                                ? optOrList
                                : optOrList
                                ? [optOrList]
                                : []
                        )
                        .map((opt) => ({
                            item_code: opt.item_code,
                            standard_rate: Number(opt.added_price) || 0,
                            qty: opt?.qty || 1,
                        }));
                    return [...selectedAddons, ...optionsAddons];
                })(),
                notes,
            };

            if (updatedEntry.item) {
                updatedEntry.item.qty = qty;
                updatedEntry.item.addons = (() => {
                    const optionsAddons = Object.values(
                        selectedOptions || {}
                    )
                        .flatMap((optOrList) =>
                            Array.isArray(optOrList)
                                ? optOrList
                                : optOrList
                                ? [optOrList]
                                : []
                        )
                        .map((opt) => ({
                            item_code: opt.item_code,
                            standard_rate: Number(opt.added_price) || 0,
                            qty: opt?.qty || 1,
                        }));
                    return [...selectedAddons, ...optionsAddons];
                })();
                updatedEntry.item.notes = notes;
                // Update the rate if admin edited it
                if (isRoleAdmin(userInfo.role_profile_name)) {
                    updatedEntry.item.standard_rate = itemRate;
                }
            }
            onUpdateItem(updatedEntry, false);
        } else {
            // Create item with potentially edited rate for admin users
            const itemToAdd = {
                ...item,
                qty: qty,
                notes: notes,
                // Use edited rate if admin, otherwise use original rate
                standard_rate: isRoleAdmin(userInfo.role_profile_name)
                    ? itemRate
                    : item.standard_rate,
            };
            const optionsAddons = Object.values(selectedOptions || {})
                .flatMap((optOrList) =>
                    Array.isArray(optOrList)
                        ? optOrList
                        : optOrList
                        ? [optOrList]
                        : []
                )
                .map((opt) => ({
                    item_code: opt.item_code,
                    standard_rate: Number(opt.added_price) || 0,
                    qty: opt?.qty || 1,
                }));
            const combinedAddons = [...selectedAddons, ...optionsAddons];
            onAddItem(itemToAdd, qty, combinedAddons, notes, true);
        }
        handleClose();
        // setTimeout(reset, 1000);
    };

    const handleCheckboxChange = (event) => {
        const { checked, value } = event.target;

        setSelectedAddons((prev) => {
            if (checked) {
                return _.xor(prev, [value]);
            } else {
                return _.without(selectedAddons, value);
            }
        });
    };

    const handlePriceChange = (event) => {
        const value = event.target.value;
        // Allow empty string during editing
        if (value === "") {
            setItemRate("");
        } else {
            const newPrice = parseFloat(value);
            if (!isNaN(newPrice) && newPrice >= 0) {
                setItemRate(newPrice);
            }
        }
    };

    const handlePriceFocus = (event) => {
        // Select all text when field is focused
        event.target.select();
    };

    const handlePriceEditStart = () => {
        setIsEditingPrice(true);
    };

    const handlePriceEditSave = () => {
        // If field is empty or invalid, revert to original value
        if (itemRate === "" || itemRate < 0 || isNaN(itemRate)) {
            const originalRate = editedOrderItem
                ? editedOrderItem.item.standard_rate
                : item.standard_rate;
            setItemRate(originalRate);
        }
        setIsEditingPrice(false);
    };

    const handlePriceEditCancel = () => {
        // Revert to original value
        const originalRate = editedOrderItem
            ? editedOrderItem.item.standard_rate
            : item.standard_rate;
        setItemRate(originalRate);
        setIsEditingPrice(false);
    };

    const handlePriceKeyPress = (event) => {
        if (event.key === "Enter") {
            handlePriceEditSave();
        } else if (event.key === "Escape") {
            handlePriceEditCancel();
        }
    };

    return item ? (
        <>
            <ItemDialog
                className={classes.DialogWrap}
                onClose={() => {
                    handleClose();
                }}
                open={open}
                PaperProps={
                    isTabsPos
                        ? {
                              style: {
                                  width: "90vw",
                                  maxWidth: "unset",
                              },
                          }
                        : undefined
                }
            >
                <DialogContent className={classes.ContentWrapper}>
                    <div>
                        <button className="BackBtn">
                            <img
                                src={backIcon}
                                onClick={() => {
                                    handleClose();
                                }}
                                alt={t("common.back")}
                            />
                        </button>
                    </div>

                    <div className={classes.Header}>
                        <div className={classes.ItemDesc}>
                            <img
                                className={classes.ItemDescImage}
                                src={item.cover}
                                alt={item.item_code}
                            />
                            <div>
                                <div className={classes.ItemDescName}>
                                    {item.item_code}
                                </div>
                                {isRoleAdmin(userInfo.role_profile_name) ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        {isEditingPrice ? (
                                            <TextField
                                                type="number"
                                                value={itemRate}
                                                onChange={handlePriceChange}
                                                onFocus={handlePriceFocus}
                                                onKeyDown={handlePriceKeyPress}
                                                onBlur={handlePriceEditSave}
                                                variant="outlined"
                                                size="small"
                                                autoFocus
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.01,
                                                    style: { fontSize: "14px" },
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root":
                                                        {
                                                            height: "32px",
                                                            "& fieldset": {
                                                                borderColor:
                                                                    "#ddd",
                                                            },
                                                            "&:hover fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#999",
                                                                },
                                                            "&.Mui-focused fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#1976d2",
                                                                },
                                                        },
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <div
                                                    className={
                                                        classes.ItemDescPrice
                                                    }
                                                >
                                                    {itemRate || 0}
                                                </div>
                                                {elevatedPrivileges ||
                                                isRoleAdmin(
                                                    userInfo.role_profile_name
                                                ) ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={
                                                            handlePriceEditStart
                                                        }
                                                        sx={{
                                                            padding: "4px",
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "rgba(0, 0, 0, 0.04)",
                                                            },
                                                        }}
                                                    >
                                                        ✏️
                                                    </IconButton>
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className={classes.ItemDescPrice}>
                                        {item.standard_rate}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.ItemQty}>
                            <ItemSmallButton
                                onClick={() => addQty(-1)}
                                className={classes.ItemQtyMinus}
                            >
                                -
                            </ItemSmallButton>
                            <NumPad.Number
                                onChange={(value) => {
                                    setQty(value > 0 ? toInteger(value) : 1);
                                }}
                                isDecimal={false}
                                position={"startBottomRight"}
                                min={1}
                                value={0}
                            >
                                <div className={classes.ItemQtyText}>{qty}</div>
                            </NumPad.Number>
                            <ItemSmallButton
                                onClick={() => addQty(1)}
                                className={classes.ItemQtyPlus}
                            >
                                +
                            </ItemSmallButton>
                        </div>
                    </div>

                    <div
                        className={classes.ScrollableContent}
                        style={{
                            overflow: menuBundle && menuBundle.categories ? "hidden" : "auto",
                        }}
                    >
                        {menuBundle && menuBundle.categories ? (
                            <BundleConfigurator
                                item={item}
                                menuBundle={bundleDraft || menuBundle}
                                addonItems={addonItems}
                                menuAddons={menuAddons}
                                isTabsPos={!!isTabsPos}
                                selectedOptions={selectedOptions}
                                setSelectedOptions={setSelectedOptions}
                                selectedAddons={selectedAddons}
                                setSelectedAddons={setSelectedAddons}
                                bundleStep={bundleStep}
                                setBundleStep={setBundleStep}
                                bundleErrors={bundleErrors}
                                setBundleErrors={setBundleErrors}
                                classes={classes}
                                qty={qty}
                                reorderMode={reorderMode}
                                onToggleReorder={(next) => setReorderMode(!!next)}
                                onReorderCategories={({ oldIndex, newIndex }) => {
                                    let nextCategories = null;
                                    setBundleDraft((prev) => {
                                        if (!prev || !Array.isArray(prev.categories)) return prev;
                                        nextCategories = arrayMove(prev.categories, oldIndex, newIndex);
                                        return {
                                            ...prev,
                                            categories: nextCategories,
                                        };
                                    });
                                    if (nextCategories) {
                                        persistCategoryOrderForItem(nextCategories);
                                    }
                                    setBundleStep((prevStep) => {
                                        // Keep the same category selected after reorder when possible.
                                        if (prevStep === oldIndex) return newIndex;
                                        if (oldIndex < prevStep && newIndex >= prevStep) return prevStep - 1;
                                        if (oldIndex > prevStep && newIndex <= prevStep) return prevStep + 1;
                                        return prevStep;
                                    });
                                }}
                                onReorderOptions={({ categoryName, oldIndex, newIndex }) => {
                                    if (!categoryName) return;
                                    let nextOptions = null;
                                    setBundleDraft((prev) => {
                                        if (!prev || !Array.isArray(prev.categories)) return prev;
                                        const cats = prev.categories.map((c) => {
                                            if (c?.name !== categoryName) return c;
                                            const opts = Array.isArray(c?.options) ? c.options : [];
                                            nextOptions = arrayMove(opts, oldIndex, newIndex);
                                            return { ...c, options: nextOptions };
                                        });
                                        return { ...prev, categories: cats };
                                    });
                                    if (nextOptions) {
                                        persistOptionOrderForItem(categoryName, nextOptions);
                                    }
                                }}
                                notes={notes}
                                setNotes={setNotes}
                            />
                        ) : (
                            <>
                                 {hasExtraAddons ? (
                                     <div className={classes.Addons}>
                                        <div>{t("bundle.extraAddons")}</div>
                                         <FormGroup
                                             className={classes.AddonsList}
                                         >
                                             {item.allowed_addons
                                                 ? item.allowed_addons.map(
                                                       (addonsItem) => {
                                                           const addon =
                                                               addonItems.find(
                                                                   (i) =>
                                                                       i.item_code ===
                                                                       addonsItem
                                                               ) || {};
                                                           return (
                                                               <div
                                                                   className={
                                                                       classes.AddonsRaw
                                                                   }
                                                                   key={addonsItem}
                                                               >
                                                                   <FormControlLabel
                                                                       control={
                                                                           <Checkbox
                                                                               onChange={
                                                                                   handleCheckboxChange
                                                                               }
                                                                               checked={
                                                                                   selectedAddons.find(
                                                                                       (i) =>
                                                                                           i ===
                                                                                           addonsItem
                                                                                   ) !=
                                                                                   null
                                                                               }
                                                                               value={
                                                                                   addonsItem
                                                                               }
                                                                           />
                                                                       }
                                                                       label={`${addonsItem} `}
                                                                   />
                                                                   <span>
                                                                       {addon.standard_rate}
                                                                   </span>
                                                               </div>
                                                           );
                                                       }
                                                   )
                                                 : ""}
                                         </FormGroup>
                                     </div>
                                 ) : null}
                            </>
                        )}

                        {/* Notes: standalone only when there is no bundle */}
                        {!(menuBundle && menuBundle.categories) ? (
                            <div className={classes.Notes}>
                                <div>{t("bundle.notes")}</div>
                                <textarea
                                    value={notes}
                                    onChange={handleNotesChange}
                                    placeholder={t("bundle.addNotesHere")}
                                    className={
                                        isNotesOnly
                                            ? `${classes.NotesArea} ${classes.NotesAreaLarge}`
                                            : classes.NotesArea
                                    }
                                ></textarea>
                            </div>
                        ) : null}
                    </div>

                    <div className={classes.Footer}>
                        <div>
                            <div className={classes.TotalText}>
                                {t("pos.total")}
                            </div>
                            <div>
                                <div className={classes.TotalPrice}>
                                    {total}
                                </div>
                                <div className={classes.AddonsPrice}>
                                    ({t("bundle.addonsLabel")} {addonsTotal})
                                </div>
                            </div>
                        </div>
                        {isTabsPos ? (
                            <div className={classes.FooterActions}>
                                <ButtonBase
                                    onClick={handleClose}
                                    className={classes.CancelAction}
                                    disabled={pushItemWorker.isLoading}
                                >
                                    {t("common.cancel")}
                                </ButtonBase>
                                <ButtonBase
                                    onClick={handleClick}
                                    className={classes.DialogAction}
                                    disabled={pushItemWorker.isLoading}
                                >
                                    {pushItemWorker.isLoading
                                        ? t("common.saving")
                                        : t("common.save")}
                                </ButtonBase>
                            </div>
                        ) : (
                            <ButtonBase
                                onClick={handleClick}
                                className={classes.DialogAction}
                            >
                                {pushItemWorker.isLoading
                                    ? t("common.saving")
                                    : t("common.save")}
                            </ButtonBase>
                        )}
                    </div>
                </DialogContent>
            </ItemDialog>
        </>
    ) : (
        ""
    );
}

export default AddItemWidget;
